import { SessionCutoff } from "@/components/SessionCutoff";
import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ActiveTaskBadge from "@/components/ActiveTaskBadge";
import GlobalActivityModals from "@/components/GlobalActivityModals";
import { LogOut, BookOpen, Home as HomeIcon, ArrowLeft } from "lucide-react";
import { visibleSections, sectionForPath, isLinkActive } from "@/lib/navSections";
import { hasModule } from "@/lib/access";
import { Button } from "@/components/ui/button";

const linkClass = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-2.5 text-sm rounded-md border transition-[background-color,border-color] ${
    isActive
      ? "bg-blue-700 text-white border-blue-700 shadow-sm"
      : "bg-white text-slate-700 border-transparent hover:border-blue-100 hover:bg-blue-50 hover:text-blue-800"
  }`;

const useFollowupBadge = (on) => {
  const { pathname } = useLocation();
  const [n, setN] = useState(0);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    window.addEventListener("inquiries:changed", bump);
    return () => window.removeEventListener("inquiries:changed", bump);
  }, []);
  useEffect(() => {
    if (!on) return;
    import("@/lib/api").then((m) => m.api.get("/inquiries/followups/summary")).then((r) => setN(r.data.overdue + r.data.today)).catch(() => {});
  }, [on, pathname, tick]);
  return n;
};

const SectionLinks = ({ section, badges = {} }) => {
  const { pathname } = useLocation();
  return section.links.map((l) => (
  <NavLink key={l.to} data-testid={l.testid} to={l.to} end={l.end} className={({ isActive }) => linkClass({ isActive: isActive || isLinkActive(pathname, l) })}>
    <l.icon className="w-4 h-4" strokeWidth={1.5} /> {l.label}
    {l.badge && badges[l.badge] > 0 && <span data-testid={`badge-${l.badge}`} className="ml-auto text-[10px] font-semibold min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white flex items-center justify-center">{badges[l.badge]}</span>}
  </NavLink>
  ));
};

const SidebarNav = ({ sections, current, badges }) => (
  <nav className="p-3 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
    <NavLink data-testid="nav-home" to="/" end className={linkClass}>
      {current ? <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> : <HomeIcon className="w-4 h-4" strokeWidth={1.5} />} {current ? "All sections" : "Home"}
    </NavLink>
    {current ? (
      <>
        <div data-testid="sidebar-section-title" className={`text-[10px] uppercase tracking-wider px-2 pt-4 pb-1 flex items-center gap-1 ${current.id === "admin" ? "text-rose-400" : "text-slate-400"}`}>
          <current.icon className="w-3 h-3" /> {current.label}
        </div>
        <SectionLinks section={current} badges={badges} />
      </>
    ) : (
      <>
        <div className="text-[10px] uppercase tracking-wider text-slate-400 px-2 pt-4 pb-1">Sections</div>
        {sections.map((s) => (
          <NavLink key={s.id} data-testid={`nav-section-${s.id}`} to={s.links[0].to} className={linkClass({ isActive: false })}>
            <s.icon className="w-4 h-4" strokeWidth={1.5} /> {s.label}
          </NavLink>
        ))}
      </>
    )}
  </nav>
);

const MobileNav = ({ current }) => (
  <div className="guide-noprint md:hidden bg-white border-b border-slate-200 px-3 py-2 flex gap-2 overflow-x-auto" data-testid="mobile-section-nav">
    <NavLink data-testid="mobile-nav-home" to="/" end className={({ isActive }) => `shrink-0 flex items-center gap-1 px-3 py-1.5 text-xs rounded-full border ${isActive ? "bg-blue-700 text-white border-blue-700" : "border-slate-200 text-slate-700"}`}>
      <HomeIcon className="w-3.5 h-3.5" /> {current ? "Sections" : "Home"}
    </NavLink>
    {current?.links.map((l) => (
      <NavLink key={l.to} data-testid={`mobile-${l.testid}`} to={l.to} end={l.end} className={({ isActive }) => `shrink-0 px-3 py-1.5 text-xs rounded-full border ${isActive ? "bg-blue-700 text-white border-blue-700" : "border-slate-200 text-slate-700"}`}>
        {l.label}
      </NavLink>
    ))}
  </div>
);

const ROLE_LABELS = { admin: "Administrator", top_management: "Top Management", employee: "Employee" };

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => {
    try { await import("@/lib/api").then((m) => m.api.post("/activity/segment/signoff")); } catch { /* fine */ }
    logout(); navigate("/login");
  };

  const { pathname } = useLocation();
  const sections = visibleSections(user);
  const current = sectionForPath(sections, pathname);
  const followupCount = useFollowupBadge(hasModule(user, "inquiries"));

  return (
    <div className="App min-h-screen flex">
      <aside data-testid="sidebar" className="guide-noprint hidden md:flex w-64 shrink-0 bg-white border-r border-slate-200 flex-col">
        <div className="px-5 py-6 border-b border-slate-200 flex items-center gap-3">
          <img src="/kms-logo.png" alt="KMS logo" className="w-10 h-10 rounded-full object-contain" data-testid="sidebar-logo" />
          <div>
            <div className="text-base font-semibold tracking-tight leading-none">KMS-Obligo</div>
            <div className="text-[11px] text-slate-500 mt-1 tracking-wide uppercase">Admin suite</div>
          </div>
        </div>
        <SidebarNav sections={sections} current={current} badges={{ followups: followupCount }} />
        <div className="mt-auto p-4 border-t border-slate-200">
          <div className="text-xs text-slate-500">Signed in as</div>
          <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-xs text-slate-500 truncate mono">{user?.email}</div>
          {user?.role && (
            <div className="mt-1"><span data-testid="sidebar-user-role" className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${user.role === "admin" ? "bg-rose-50 text-rose-700 border-rose-200" : user.role === "top_management" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>{ROLE_LABELS[user.role]}</span></div>
          )}
          <Button data-testid="logout-button" variant="outline" size="sm" className="w-full mt-3 justify-start gap-2" onClick={handleLogout}>
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="guide-noprint md:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <div className="font-semibold tracking-tight flex items-center gap-2">
            <img src="/kms-logo.png" alt="KMS" className="w-6 h-6 rounded-full object-contain" />
            KMS-Obligo
          </div>
          <div className="flex items-center gap-1">
            <Button data-testid="mobile-user-guide" variant="ghost" size="sm" onClick={() => navigate("/guide")} aria-label="User Guide">
              <BookOpen className="w-4 h-4" />
            </Button>
            <Button data-testid="mobile-logout" variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </header>
        <MobileNav current={current} />
        {/* Persistent top bar (all sizes): houses the global Active Task pill */}
        <div className="guide-noprint flex justify-end items-center gap-3 px-4 md:px-10 pt-3 pb-2 min-h-[40px] bg-slate-50 border-b border-slate-100">
          {hasModule(user, "tasks") && <ActiveTaskBadge />}
        </div>
        <div className="px-6 md:px-10 pb-8 pt-4 max-w-[1400px]">
          <Outlet />
        </div>
      </main>
      {hasModule(user, "tasks") && <GlobalActivityModals />}
      <SessionCutoff />
    </div>
  );
}
